import { useEffect, useState } from 'react';
import {
  AccountModel,
  CoQuanThue,
  DiaChi,
  DonViCoSo,
  HanThanhToan,
  KhachHang,
  NgoaiTe,
  NhanVien,
  PhuongThucGiaoHang,
  PhuongTienVanChuyen,
  QuyenChungTu
} from '@/types/schemas';
import { useAuth } from '@/contexts/auth-context';
import { QUERY_KEYS } from '@/constants';
import api from '@/lib/api';

// GetEndpoints from BasicInfoTab
const GetEndPoints = [
  {
    ma_ct: 'CN5',
    endPoint: QUERY_KEYS.CHUNG_TU_PHAI_TRA_KHAC
  },
  {
    ma_ct: 'CN6',
    endPoint: QUERY_KEYS.BUT_TOAN_DIEU_CHINH_GIAM_CONG_NO
  },
  {
    ma_ct: 'PK1',
    endPoint: QUERY_KEYS.PHIEU_KE_TOAN
  }
];

export interface FormFieldState {
  // Customer information
  khachHang: KhachHang | null;
  nhanVien: NhanVien | null;
  taiKhoan: AccountModel | null;
  hanThanhToan: HanThanhToan | null;

  // Document information
  chungTuGoc: any;
  quyenChungTu: QuyenChungTu | null;
  ngoaiTe: NgoaiTe | null;
  donViCoSo: DonViCoSo | null;

  // Other information
  diaChi: DiaChi | null;
  phuongTienVanChuyen: PhuongTienVanChuyen | null;
  phuongTienGiaoHang: PhuongThucGiaoHang | null;
  cucThue: KhachHang | null;
  coQuanThue: CoQuanThue | null;

  // So chung tu
  soChungTu: string;
  ty_gia: number;
  dienGiai: string;

  // Checkbox states
  hdbh_yn: boolean;
  px_yn: boolean;
  pt_tao_yn: boolean;
  ck_yn: boolean;
  loai_ck: string;

  // Payment method
  ma_httt: string;
  ma_ngv: string;

  // Detail data from selected document
  chungTuChiTiet: any[];

  // ID of the selected source document
  id_goc: string;
}

export interface FormFieldActions {
  // Search field setters
  setKhachHang: (khachHang: KhachHang) => void;
  setNhanVien: (nhanVien: NhanVien) => void;
  setTaiKhoan: (taiKhoan: AccountModel) => void;
  setHanThanhToan: (hanThanhToan: HanThanhToan) => void;
  setChungTuGoc: (chungTuGoc: any) => void;
  setQuyenChungTu: (quyenChungTu: QuyenChungTu) => void;
  setNgoaiTe: (ngoaiTe: NgoaiTe) => void;
  setDonViCoSo: (donViCoSo: DonViCoSo) => void;

  setDiaChi: (diaChi: DiaChi) => void;
  setPhuongTienVanChuyen: (phuongTienVanChuyen: PhuongTienVanChuyen) => void;
  setPhuongTienGiaoHang: (phuongTienGiaoHang: PhuongThucGiaoHang) => void;
  setCucThue: (cucThue: KhachHang) => void;
  setCoQuanThue: (coQuanThue: CoQuanThue) => void;

  // So chung tu
  setSoChungTu: (soChungTu: string) => void;
  setTyGia: (tyGia: number) => void;
  setDienGiai: (dienGiai: string) => void;

  // Checkbox setters
  setHdbhYn: (value: boolean) => void;
  setPxYn: (value: boolean) => void;
  setPtTaoYn: (value: boolean) => void;
  setCkYn: (value: boolean) => void;
  setLoaiCk: (value: string) => void;

  // Payment method setter
  setMaHttt: (value: string) => void;
  setMaNgv: (value: string) => void;

  // Detail data setter
  setChungTuChiTiet: (data: any[]) => void;

  // ID goc setter
  setIdGoc: (id: string) => void;

  // Utility functions
  resetState: () => void;
  updateState: (updates: Partial<FormFieldState>) => void;
}

export interface UseFormFieldStateReturn {
  state: FormFieldState;
  actions: FormFieldActions;
}

const initialState: FormFieldState = {
  // Customer information
  khachHang: null,
  nhanVien: null,
  taiKhoan: null,
  hanThanhToan: null,

  // Document information
  chungTuGoc: null,
  quyenChungTu: null,
  ngoaiTe: null,
  donViCoSo: null,

  // Other information
  diaChi: null,
  phuongTienVanChuyen: null,
  phuongTienGiaoHang: null,
  cucThue: null,
  coQuanThue: null,

  // So chung tu
  soChungTu: '',
  ty_gia: 1,
  dienGiai: '',

  // Checkbox states
  hdbh_yn: true,
  px_yn: true,
  pt_tao_yn: false,
  ck_yn: false,
  loai_ck: '2',

  // Payment method
  ma_httt: 'TMB',
  ma_ngv: '1',

  // Detail data from selected document
  chungTuChiTiet: [],

  // ID of the selected source document
  id_goc: ''
};

function transformInitialData(initialData?: any): Partial<FormFieldState> {
  if (!initialData) return {};

  return {
    // Customer information
    khachHang: initialData.ma_kh_data || null,
    nhanVien: initialData.ma_nv_data || null,
    taiKhoan: initialData.tk_data || null,
    hanThanhToan: initialData.han_tt_data || null,

    // Document information
    chungTuGoc: initialData.chung_tu_goc_data || null,
    quyenChungTu: initialData.so_ct_data || null,
    ngoaiTe: initialData.ma_nt_data || null,
    donViCoSo: initialData.unit_id_data || null,

    // Other information
    diaChi: initialData.ma_dc_data || null,
    phuongTienVanChuyen: initialData.ma_ptvc_data || null,
    phuongTienGiaoHang: initialData.ma_ptgh_data || null,
    cucThue: initialData.ma_kh9_data || null,
    coQuanThue: initialData.ma_kh9_data || null,

    // So chung tu
    soChungTu: initialData.so_ct || '',
    ty_gia: initialData.ty_gia || 1,
    dienGiai: initialData.dien_giai || '',

    // Checkbox states
    hdbh_yn: initialData.hdbh_yn ?? true,
    px_yn: initialData.px_yn ?? true,
    pt_tao_yn: initialData.pt_tao_yn ?? false,
    ck_yn: initialData.ck_yn ?? false,
    loai_ck: initialData.loai_ck || '2',

    // Payment method
    ma_httt: initialData.ma_httt || 'TMB',
    ma_ngv: initialData.ma_ngv || '1',

    // Detail data from selected document
    chungTuChiTiet: initialData.chungTuChiTiet || [],

    // ID of the selected source document
    id_goc: initialData.id_goc || ''
  };
}

export function useFormFieldState(initialData?: any): UseFormFieldStateReturn {
  const { entity } = useAuth();
  const [state, setState] = useState<FormFieldState>({
    ...initialState,
    ...transformInitialData(initialData)
  });

  useEffect(() => {
    const fetchChungTuGocFromAllEndpoints = async () => {
      if (initialData?.id_goc && !initialData?.chung_tu_goc_data && entity?.slug) {
        try {
          const promises = GetEndPoints.map(async endpoint => {
            try {
              const response = await api.get(`/entities/${entity.slug}/erp/${endpoint.endPoint}/`);

              const foundItem = response.data?.results?.find((item: any) => item.uuid === initialData.id_goc);

              return foundItem || null;
            } catch (error) {
              return null;
            }
          });

          const results = await Promise.all(promises);

          const chungTuGoc = results.find(result => result !== null);

          if (chungTuGoc) {
            setState(prev => ({
              ...prev,
              chungTuGoc: chungTuGoc
            }));
          }
        } catch (error) {
          // Silently fail - chung tu goc is optional
        }
      }
    };

    fetchChungTuGocFromAllEndpoints();
  }, [initialData?.id_goc, initialData?.chung_tu_goc_data, entity?.slug]);

  const actions: FormFieldActions = {
    // Search field setters
    setKhachHang: (khachHang: KhachHang) => {
      setState(prev => ({
        ...prev,
        khachHang
      }));
    },

    setNhanVien: (nhanVien: NhanVien) => {
      setState(prev => ({
        ...prev,
        nhanVien
      }));
    },

    setTaiKhoan: (taiKhoan: AccountModel) => {
      setState(prev => ({
        ...prev,
        taiKhoan
      }));
    },

    setHanThanhToan: (hanThanhToan: HanThanhToan) => {
      setState(prev => ({
        ...prev,
        hanThanhToan
      }));
    },

    setChungTuGoc: (chungTuGoc: any) => {
      setState(prev => ({
        ...prev,
        chungTuGoc
      }));
    },

    setQuyenChungTu: (quyenChungTu: QuyenChungTu) => {
      setState(prev => ({
        ...prev,
        quyenChungTu
      }));
    },

    setNgoaiTe: (ngoaiTe: NgoaiTe) => {
      setState(prev => ({
        ...prev,
        ngoaiTe
      }));
    },

    setDonViCoSo: (donViCoSo: DonViCoSo) => {
      setState(prev => ({
        ...prev,
        donViCoSo
      }));
    },

    setDiaChi: (diaChi: DiaChi) => {
      setState(prev => ({
        ...prev,
        diaChi
      }));
    },

    setPhuongTienVanChuyen: (phuongTienVanChuyen: PhuongTienVanChuyen) => {
      setState(prev => ({
        ...prev,
        phuongTienVanChuyen
      }));
    },

    setPhuongTienGiaoHang: (phuongTienGiaoHang: PhuongThucGiaoHang) => {
      setState(prev => ({
        ...prev,
        phuongTienGiaoHang
      }));
    },

    setCucThue: (cucThue: KhachHang) => {
      setState(prev => ({
        ...prev,
        cucThue
      }));
    },

    setCoQuanThue: (coQuanThue: CoQuanThue) => {
      setState(prev => ({
        ...prev,
        coQuanThue
      }));
    },

    // So chung tu
    setSoChungTu: (soChungTu: string) => {
      setState(prev => ({ ...prev, soChungTu }));
    },

    setTyGia: (tyGia: number) => {
      setState(prev => ({ ...prev, ty_gia: tyGia }));
    },

    setDienGiai: (dienGiai: string) => {
      setState(prev => ({ ...prev, dienGiai }));
    },

    // Checkbox setters
    setHdbhYn: (value: boolean) => {
      setState(prev => ({ ...prev, hdbh_yn: value }));
    },

    setPxYn: (value: boolean) => {
      setState(prev => ({ ...prev, px_yn: value }));
    },

    setPtTaoYn: (value: boolean) => {
      setState(prev => ({ ...prev, pt_tao_yn: value }));
    },

    setCkYn: (value: boolean) => {
      setState(prev => ({ ...prev, ck_yn: value }));
    },

    setLoaiCk: (value: string) => {
      setState(prev => ({ ...prev, loai_ck: value }));
    },

    // Payment method setter
    setMaHttt: (value: string) => {
      setState(prev => ({ ...prev, ma_httt: value }));
    },

    setMaNgv: (value: string) => {
      setState(prev => ({ ...prev, ma_ngv: value }));
    },

    // Detail data setter
    setChungTuChiTiet: (data: any[]) => {
      setState(prev => ({ ...prev, chungTuChiTiet: data }));
    },

    // ID goc setter
    setIdGoc: (id: string) => {
      setState(prev => ({ ...prev, id_goc: id }));
    },

    resetState: () => {
      setState(initialState);
    },

    updateState: (updates: Partial<FormFieldState>) => {
      setState(prev => ({ ...prev, ...updates }));
    }
  };

  return { state, actions };
}
